import { AlertCircle, ArrowRight, Loader2, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { UnityLogo } from '@/components/brand/Logo'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/auth'

/** Explanations for the failure codes the callback can redirect back with. */
const REASONS: Record<string, { title: string; detail: string }> = {
  no_console_access: {
    title: 'No console access',
    detail:
      'Your Discord roles do not grant access to the Unity console. If you believe this is wrong, ask an administrator to map your role in Settings.',
  },
  not_a_guild_member: {
    title: 'Not a member',
    detail: 'You are not in the Unity Discord server, so there is nothing to sign in to.',
  },
  account_disabled: {
    title: 'Access disabled',
    detail: 'An administrator has disabled console access for your account.',
  },
  discord_not_configured: {
    title: 'Not configured',
    detail:
      'This deployment has no Discord application credentials yet. Set them in backend/.env and restart the API.',
  },
  oauth_state_expired: {
    title: 'Sign-in link expired',
    detail: 'That sign-in attempt took too long. Start again.',
  },
  oauth_state_reused: {
    title: 'Sign-in link already used',
    detail: 'Each sign-in link works once. Start again.',
  },
  session_expired: {
    title: 'Session expired',
    detail: 'You were signed out after a period of inactivity.',
  },
  access_denied: {
    title: 'Sign-in cancelled',
    detail: 'You declined the Discord authorisation prompt.',
  },
}

export function LoginPage() {
  const [params] = useSearchParams()
  const [redirecting, setRedirecting] = useState(false)
  const loginError = useAuthStore((state) => state.loginError)
  const setLoginError = useAuthStore((state) => state.setLoginError)

  const code = params.get('error') ?? loginError?.code
  const message = params.get('message') ?? loginError?.message
  const reason = code ? (REASONS[code] ?? { title: 'Sign-in failed', detail: message ?? '' }) : null

  useEffect(() => {
    // The store-held error is consumed on mount so it does not persist across
    // a later successful sign-in.
    return () => setLoginError(null)
  }, [setLoginError])

  const signIn = () => {
    setRedirecting(true)
    const next = params.get('next')
    const query = next ? `?redirect_to=${encodeURIComponent(next)}` : ''
    window.location.href = `/api/v1/auth/discord/login${query}`
  }

  return (
    <div className="bg-background relative flex min-h-dvh items-center justify-center overflow-hidden p-6">
      {/* The login screen is the one place the brand gets to speak, so it
          reproduces the logo's own ground: navy lifting toward the centre, over
          a faint grid. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 90% 70% at 50% 8%, #24325a 0%, #16203a 45%, #0b111d 100%)',
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #ffffff0a 1px, transparent 1px),' +
            'linear-gradient(to bottom, #ffffff0a 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, #000 30%, transparent 80%)',
        }}
        aria-hidden
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <UnityLogo className="mb-4 h-24 w-auto" />
          <p className="text-[13px] tracking-[0.18em] text-white/45 uppercase">Staff console</p>
        </div>

        <div className="bg-surface border-border elevated rounded-xl border p-6">
          {reason && (
            <div className="bg-danger-soft mb-5 flex items-start gap-2.5 rounded-lg p-3">
              <AlertCircle
                className="text-danger mt-px size-4 shrink-0"
                strokeWidth={2}
                aria-hidden
              />
              <div className="min-w-0">
                <p className="text-danger text-[12.5px] font-semibold">{reason.title}</p>
                <p className="text-danger/85 mt-0.5 text-[12px] leading-relaxed">{reason.detail}</p>
              </div>
            </div>
          )}

          <button
            onClick={signIn}
            disabled={redirecting}
            className={cn(
              'flex h-11 w-full items-center justify-center gap-2.5 rounded-lg',
              'bg-[#5865F2] font-medium text-white transition-colors hover:bg-[#4752C4]',
              'disabled:cursor-not-allowed disabled:opacity-70',
            )}
          >
            {redirecting ? (
              <Loader2 className="size-4 animate-spin" strokeWidth={2} aria-hidden />
            ) : (
              <svg className="size-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.099.246.197.373.291a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03ZM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z" />
              </svg>
            )}
            {redirecting ? 'Redirecting to Discord…' : 'Continue with Discord'}
            {!redirecting && (
              <ArrowRight className="size-4 opacity-70" strokeWidth={2} aria-hidden />
            )}
          </button>

          <div className="text-muted-foreground mt-5 flex items-start gap-2 text-[11.5px] leading-relaxed">
            <ShieldCheck className="mt-px size-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            <p>
              Access is granted by your Discord roles. Your guild membership is verified on every
              sign-in and re-checked periodically while you work.
            </p>
          </div>
        </div>

        <p className="text-subtle-foreground mt-6 text-center text-[11px]">
          Unity RP · Staff console
        </p>
      </div>
    </div>
  )
}
